import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-toggle-css.js';
import type { PropertyValues } from 'lit';

/**
 * A form control toggle switch.
 *
 * @element zui-toggle
 *
 * @slot - Default, unnamed slot; not recommended for use
 *
 * @attr {string | null} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @attr {boolean} [readonly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @attr {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @prop {boolean} [readOnly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @prop {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 *
 * @cssprop [--zui-toggle-checked-color=var(--zui-blue)] - The background fill color of the toggle track
 * @cssprop [--zui-toggle-checked-disabled-color=var(--zui-blue-100)] - Disabled state background fill color of the toggle track
 */
export class ZuiToggle extends ZuiFormAssociatedElement {
  protected get _formValue(): string | null {
    return this.checked ? this.value : null;
  }

  protected get _focusControlSelector() {
    return '.handle';
  }

  /**
   * Whether the toggle is "on" or "off"
   */
  @property({ type: Boolean, reflect: true }) checked = false;

  /**
   * Toggle's `value` that is form submittable, along with `name`
   */
  @property({ type: String, reflect: true }) value = 'on';

  /**
   * @deprecated `standard` is the only type supported and doesn't need to be set
   * The type of toggle. Supports `standard` and `icon`; defaults to `standard`
   * If `icon`, provide an icon to the slot to be toggled
   */
  @property({ type: String })
  type = 'standard';

  #initialState: boolean;

  @query('#icon')
  private _icon: HTMLElement | undefined;

  @query('#handle')
  private _handle: HTMLElement | undefined;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();

    this.addEventListener('click', () => {
      if (!this.disabled) {
        this.checked = !this.checked;
        this._setFormValue(this._formValue);
        this.dispatchEvent(new CustomEvent('change', { detail: { checked: this.checked } }));
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();

    // we want to go a little faster than LitElement and behave more like native HTML Form Associated Elements
    const initialChecked = this.hasAttribute('checked');
    const initialValue = this.getAttribute('value');
    this.#initialState = initialChecked;
    this._setFormValue(initialChecked ? initialValue : null);
  }

  /**
   * Reset toggle to state on initial load
   */
  formResetCallback(): void {
    this.checked = this.#initialState;
    this._setFormValue(this._formValue);
  }

  update(changedProps: PropertyValues) {
    super.update(changedProps);

    if (changedProps.has('value')) {
      this._setFormValue(this._formValue);
    }

    if (changedProps.has('disabled')) {
      this._setTabIndex();
    }
  }

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);

    this.setAttribute('role', 'checkbox');
    this._setTabIndex();

    this.addEventListener('keydown', (e) => {
      if (e.keyCode === 32) {
        if (!this.disabled) {
          this.checked = !this.checked;
          if (this.type === 'icon') {
            this._icon?.classList.toggle('burst', true);
          }
          e.preventDefault();
          e.stopPropagation();
        }
      }
    });

    this.addEventListener('click', () => {
      if (this.type === 'icon' && !this.disabled) {
        this._icon?.classList.toggle('burst', true);
      }
      if (this.type !== 'icon' && this.disabled) {
        this._handle?.classList.toggle('wiggle', true);
      }
    });
  }

  render() {
    const tabIndex = this.disabled ? '-1' : '0';

    return html`
      ${
        this.type === 'icon'
          ? html`
              <div
                id="icon"
                @animationend="${(e: AnimationEvent) => {
                  (e.currentTarget as HTMLElement).classList.remove('burst');
                }}"
                class="icon"
                ><slot></slot
              ></div>
            `
          : html`
              <div class="container">
                <div
                  id="handle"
                  class="handle"
                  tabindex="${tabIndex}"
                  @animationend="${(e: AnimationEvent) => {
                    (e.currentTarget as HTMLElement).classList.remove('wiggle');
                  }}"
                ></div>
              </div>
            `
      }
      </div>
    `;
  }

  private _setTabIndex() {
    if (this.disabled) {
      this.removeAttribute('tabindex');
    } else {
      this.setAttribute('tabindex', '0');
    }
  }
}

window.customElements.define('zui-toggle', ZuiToggle);

declare global {
  interface HTMLElementTagNameMap {
    'zui-toggle': ZuiToggle;
  }
}
